import { useState, useRef } from 'react'
import { Dropzone } from '@mantine/dropzone'
import { Text, Stack, Center, Progress, Group, Button } from '@mantine/core'
import { IconUpload, IconVideo, IconX, IconPlayerStop, IconRefresh } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { uploadVideoChunked } from '@/lib/upload'
import VideoPlayer from '@/components/VideoPlayer'

const VIDEO_MIME = ['video/mp4', 'video/webm', 'video/quicktime']
const MAX_SIZE = 5 * 1024 * 1024 * 1024 // 5GB

function formatSize(bytes: number) {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)}GB`
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(0)}MB`
  return `${(bytes / 1024).toFixed(0)}KB`
}

interface VideoUploadProps {
  value?: string
  onChange: (url: string) => void
  onFileSelected?: (file: File) => void
  videoId?: string
}

export default function VideoUpload({ value, onChange, onFileSelected, videoId }: VideoUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [fileName, setFileName] = useState('')
  const abortRef = useRef<AbortController | null>(null)

  const handleDrop = async (files: File[]) => {
    const file = files[0]
    if (!file) return

    if (file.size > MAX_SIZE) {
      notifications.show({ title: 'Алдаа', message: '5GB-аас их файл оруулах боломжгүй', color: 'red' })
      return
    }

    onFileSelected?.(file)

    setUploading(true)
    setProgress(0)
    setFileName(`${file.name} (${formatSize(file.size)})`)

    const controller = new AbortController()
    abortRef.current = controller

    try {
      const result = await uploadVideoChunked({
        file,
        onProgress: setProgress,
        signal: controller.signal,
        videoId,
      })
      onChange(result.url)
      notifications.show({ title: 'Амжилттай', message: 'Видео хуулагдлаа', color: 'green' })
    } catch (err) {
      if ((err as Error).message !== 'Upload cancelled') {
        notifications.show({ title: 'Алдаа', message: 'Видео хуулахад алдаа гарлаа', color: 'red' })
      }
    } finally {
      setUploading(false)
      setProgress(0)
      setFileName('')
      abortRef.current = null
    }
  }

  const handleCancel = () => {
    abortRef.current?.abort()
    notifications.show({ title: 'Цуцлагдсан', message: 'Видео хуулалт цуцлагдлаа', color: 'yellow' })
  }

  return (
    <Stack gap="xs">
      {value && !uploading && (
        <Stack gap="xs">
          <VideoPlayer src={value} />
          <Group justify="center">
            <Button
              size="compact-sm"
              variant="light"
              leftSection={<IconRefresh size={14} />}
              onClick={() => onChange('')}
            >
              Дахин хуулах
            </Button>
          </Group>
        </Stack>
      )}
      {uploading && (
        <Stack gap={4}>
          <Group justify="space-between">
            <Text size="xs" c="dimmed">{fileName}</Text>
            <Group gap="xs">
              <Text size="xs" fw={600}>{progress}%</Text>
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                leftSection={<IconPlayerStop size={14} />}
                onClick={handleCancel}
              >
                Цуцлах
              </Button>
            </Group>
          </Group>
          <Progress value={progress} animated size="lg" />
          {progress > 0 && progress < 96 && (
            <Text size="xs" c="dimmed">Хуулж байна...</Text>
          )}
          {progress >= 96 && progress < 100 && (
            <Text size="xs" c="dimmed">Нэгтгэж байна...</Text>
          )}
        </Stack>
      )}
      {!value && <Dropzone
        onDrop={handleDrop}
        accept={VIDEO_MIME}
        maxSize={MAX_SIZE}
        loading={uploading}
        multiple={false}
        disabled={uploading}
      >
        <Center h={120}>
          <Dropzone.Accept>
            <IconUpload size={32} stroke={1.5} />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={32} stroke={1.5} />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <Stack align="center" gap={4}>
              <IconVideo size={32} stroke={1.5} />
              <Text size="sm" c="dimmed">Видео файл чирж оруулах эсвэл дарна уу</Text>
              <Text size="xs" c="dimmed">MP4, WebM, MOV (5GB хүртэл)</Text>
            </Stack>
          </Dropzone.Idle>
        </Center>
      </Dropzone>}
    </Stack>
  )
}
